import type { CSSProperties } from "react";
import type {
  TypographySettings,
  WebsiteTypography,
  WebsiteTypographyRole,
} from "@/cms/typography";
import { responsiveValue } from "./visual-style";

interface TypographyRole {
  selector: string;
  defaults: TypographySettings;
  inherits?: WebsiteTypographyRole;
}

export const classicTypography: Record<WebsiteTypographyRole, TypographyRole> =
  {
    body: {
      selector: "",
      defaults: {
        fontSize: "18px",
        fontWeight: 300,
        lineHeight: 1.55,
        fontRole: "body",
      },
    },
    heading: {
      selector: ":is(h1,h2,h3,h4,.primary-font)",
      defaults: { fontWeight: 500, fontRole: "heading" },
    },
    navigation: {
      selector: ".re-classic-desktop-nav",
      defaults: {
        fontSize: "18px",
        fontWeight: 500,
        lineHeight: 1,
        fontRole: "heading",
      },
    },
    submenu: {
      selector: ".re-classic-dropdown a",
      defaults: { fontSize: "16px", fontWeight: 400, lineHeight: 1.5 },
    },
    mobileNavigation: {
      selector: ".re-classic-menu-row",
      defaults: { fontSize: "20px", fontWeight: 500, lineHeight: 1.2 },
    },
    mobileSubmenu: {
      selector: ".re-classic-menu-children a",
      defaults: { fontSize: "18px", fontWeight: 400, lineHeight: 1.5 },
    },
    button: {
      selector: ".re-action-arrow, form button[type=submit]",
      defaults: {
        fontSize: "18px",
        fontWeight: 500,
        lineHeight: 1,
        fontRole: "body",
      },
    },
    formInput: {
      selector: ".MuiInputBase-root, .MuiInputBase-input",
      defaults: { fontSize: "16px", fontWeight: 400, lineHeight: 1.5 },
    },
    formLabel: {
      selector: ".MuiInputLabel-root",
      defaults: { fontSize: "14px", fontWeight: 400, lineHeight: 2 },
    },
    formHelp: {
      selector: ".MuiFormHelperText-root",
      defaults: { fontSize: "14px", fontWeight: 400, lineHeight: 1.5 },
    },
    contentCardTitle: {
      selector: ".re-content-card-media h2, .re-content-card-notice h2",
      defaults: {
        fontSize: "20px",
        fontWeight: 500,
        lineHeight: 1.2,
        fontRole: "heading",
      },
    },
    noticeBody: {
      selector: ".re-content-card-notice .styled-description",
      defaults: { fontSize: "18px", fontWeight: 400, lineHeight: 1.55 },
    },
    contactTitle: {
      selector: ".re-content-card-contact h2",
      defaults: { fontSize: "18px | 26px", fontWeight: 500, lineHeight: 1.25 },
    },
    contactBody: {
      selector:
        ".re-content-card-contact .styled-description, a.re-content-card-contact",
      defaults: { fontSize: "16px | 18px", fontWeight: 400, lineHeight: 1.5 },
    },
    listTitle: {
      selector: ".re-classic-search-heading h1",
      defaults: { fontSize: "26px | 36px", fontWeight: 500, lineHeight: 1.38 },
    },
    propertyTitle: {
      selector: ".re-classic-property-copy h2",
      defaults: { fontSize: "20px", fontWeight: 500, lineHeight: 1.2 },
    },
    propertyFacts: {
      selector: ".re-classic-property-facts",
      defaults: { fontSize: "18px", fontWeight: 400, lineHeight: 1.55 },
    },
    propertyLabel: {
      selector: ".re-classic-property-label",
      defaults: { fontSize: "16px", fontWeight: 400, lineHeight: 1.5 },
    },
    caseTitle: {
      selector: ".re-classic-case-description h1",
      defaults: { fontSize: "22px | 26px", fontWeight: 500, lineHeight: 1.22 },
    },
    caseBody: {
      selector: ".re-classic-case-description",
      defaults: { fontSize: "16px", fontWeight: 300, lineHeight: 1.55 },
    },
    caseFactsTitle: {
      selector: ".re-classic-case-facts h2",
      defaults: { fontSize: "22px | 26px", fontWeight: 500, lineHeight: 1.38 },
    },
    caseFacts: {
      selector: ".re-classic-case-facts dl > div",
      defaults: { fontSize: "18px", fontWeight: 500, lineHeight: 1.5 },
    },
    caseAction: {
      selector: ".re-cinematic-actions button",
      defaults: { fontSize: "14px | 18px", fontWeight: 400, lineHeight: 1 },
    },
    articleTitle: {
      selector: ".re-articles .a-post-copy h1",
      defaults: { fontSize: "25px | 36px", fontWeight: 500, lineHeight: 1.22 },
    },
    articleCardTitle: {
      selector: ".re-articles .a-card-copy h3",
      defaults: { fontSize: "20px", fontWeight: 500, lineHeight: 1.4 },
    },
    articleBody: {
      selector: ".re-articles .a-reading",
      defaults: { fontSize: "18px", fontWeight: 300, lineHeight: 1.55 },
    },
    articleHeading: {
      selector: ".re-articles .a-reading :is(h2,h3)",
      defaults: { fontSize: "22px | 26px", fontWeight: 500, lineHeight: 1.22 },
    },
    articleMetadata: {
      selector: ".re-articles .a-meta",
      defaults: { fontSize: "16px", fontWeight: 300, lineHeight: 1.55 },
    },
    articleButton: {
      selector: ".re-articles .a-read, .re-articles .a-card .a-read",
      defaults: {},
      inherits: "button",
    },
    featureTitle: {
      selector: ".re-articles .a-feature-split h2",
      defaults: { fontSize: "28px | 36px", fontWeight: 500, lineHeight: 1.22 },
    },
    featureBody: {
      selector: ".re-articles .a-feature-split .a-excerpt",
      defaults: { fontSize: "18px", fontWeight: 300, lineHeight: 1.55 },
    },
    dialogTitle: {
      selector: ".re-content-dialog-heading h2",
      defaults: { fontSize: "22px | 26px", fontWeight: 500, lineHeight: 1.2 },
    },
  };

function properties(settings: TypographySettings, desktop: boolean) {
  return Object.entries({
    "font-size": responsiveValue(settings.fontSize, desktop),
    "font-weight": settings.fontWeight,
    "line-height": responsiveValue(String(settings.lineHeight ?? ""), desktop),
    "letter-spacing": responsiveValue(settings.letterSpacing, desktop),
    "font-family": settings.fontRole
      ? `var(--${settings.fontRole}-font-family)`
      : undefined,
  }).filter(
    ([, value]) =>
      value !== undefined && value !== "" && !/[;{}<>]/.test(String(value)),
  );
}

export function typographyRoleStyle(
  role: WebsiteTypographyRole,
  settings?: TypographySettings,
): CSSProperties {
  if (!settings) return {};
  return Object.fromEntries(
    [false, true].flatMap((desktop) =>
      properties(settings, desktop).map(([key, value]) => [
        `--re-type-${role}-${key}${desktop ? "-desktop" : ""}`,
        String(value),
      ]),
    ),
  ) as CSSProperties;
}

export function websiteTypographyCss(
  settings?: WebsiteTypography,
  presentation?: string,
) {
  if (presentation !== "classic" && !settings) return "";
  const roles = (
    Object.keys(classicTypography) as WebsiteTypographyRole[]
  ).filter((role) => presentation === "classic" || settings?.[role]);
  const resolve = (role: WebsiteTypographyRole): TypographySettings => {
    const definition = classicTypography[role];
    return {
      ...(definition.inherits ? resolve(definition.inherits) : {}),
      ...(presentation === "classic" ? definition.defaults : {}),
      ...settings?.[role],
    };
  };
  const rules = (desktop: boolean) =>
    roles
      .map((role) => {
        const definition = classicTypography[role];
        const selectors = definition.selector
          .split(", ")
          .map(
            (selector) =>
              `body[data-re-typography]${selector ? ` ${selector}` : ""}`,
          )
          .join(", ");
        const values = properties(resolve(role), desktop)
          .map(([key, value]) => {
            const mobile = `var(--re-type-${role}-${key}, ${value})`;
            return `${key}: ${desktop ? `var(--re-type-${role}-${key}-desktop, ${mobile})` : mobile};`;
          })
          .join("\n");
        return `${selectors} { ${values} }`;
      })
      .join("\n");
  return `${rules(false)}\n@media (min-width: 768px) { ${rules(true)} }`;
}
